/**
 * Scan detail page.
 *
 * Phase 16: Product Intelligence UX.
 *
 * Shows the full detail of a completed scan:
 * - Accessibility score hero (full formula using all four severity counts)
 * - Severity breakdown strip
 * - Priority findings (top critical/serious issues)
 * - Tabbed view: Issues | Pages | Evidence
 *
 * API endpoints used:
 *   GET /reports/scans/:id/summary  — issue counts + breakdowns
 *   GET /scans/:id/issues           — issue instances + definitions
 *   GET /scans/:id/pages            — crawled pages
 *   GET /scans/:id/evidence         — evidence records
 */

import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { IssueImpact } from '@accestra/types';
import i18n from '../i18n/i18n';
import { api, fetchAuthenticated, friendlyError, ApiError } from '../lib/api-client';
import { useAuth } from '../hooks/useAuth';
import {
  calculateAccessibilityScore,
  getScoreColor,
  getScoreLabelKey,
  getScoreVariant,
} from '../lib/accessibility-score';
import FeatureLockModal from '../components/entitlements/FeatureLockModal';
import IssueReviewModal, { type IssueReviewDraft } from '../components/review/IssueReviewModal';
import ScanReviewPanel from '../components/review/ScanReviewPanel';
import Card from '../components/ui/Card';
import Badge, { BadgeVariant } from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Container from '../components/ui/Container';
import EmptyState from '../components/ui/EmptyState';
import StatCard from '../components/ui/StatCard';
import AlertBanner from '../components/ui/AlertBanner';
import PageHeader from '../components/ui/PageHeader';
import SectionHeader from '../components/ui/SectionHeader';
import ScoreCard from '../components/ui/ScoreCard';
import { buildBillingUpgradePath } from '../lib/plans';
import { copyTextToClipboard } from '../lib/partner-portal';
import { resolveScanFailurePresentation, resolveScanFailureSummary } from '../lib/scan-failures';
import { deriveScanReview } from '../lib/scan-review';
import { formatDate, formatDateTime } from '../i18n/formatters';

// -------------------------------------------------------------------
// Types
// -------------------------------------------------------------------

type IssueWorkflowState = 'open' | 'acknowledged' | 'in_progress' | 'resolved' | 'false_positive';
type IssueDeliveryStatus = 'open' | 'in_progress' | 'fixed';
type ExternalIssueStatus = 'open' | 'closed' | 'unknown';

interface IssueWorkflowSummary {
  open: number;
  acknowledged: number;
  inProgress: number;
  resolved: number;
  falsePositive: number;
  suppressed: number;
}

interface IssueSuppression {
  id: string;
  reason: string;
  pageUrl: string;
  selector: string | null;
  matchedBy: 'page' | 'selector';
  expiresAt: string | null;
  createdAt: string;
  isExpired: boolean;
}

interface IssueSummaryEntry {
  issueId: string;
  code: string;
  title: string;
  impact: IssueImpact;
  severityOverride: IssueImpact | null;
  wcagCriteria: string;
  wcagLevel: string;
  category: string;
  plainEnglishExplanation: string;
  remediationGuidance: string;
  likelyCause: string;
  developerFixGuidance: string;
  occurrenceCount: number;
  affectedPages: number;
  samplePageUrl: string | null;
  samplePageEvidenceId: string | null;
  workflowState: IssueWorkflowState;
  workflowAssigneeName: string | null;
  workflowAssigneeEmail: string | null;
  workflowNote: string | null;
  workflowUpdatedAt: string | null;
  workflowSummary: IssueWorkflowSummary;
  suppressedCount: number;
  externalLink: IssueExternalLink | null;
}

interface IssueSummaryEntryResponse extends Omit<IssueSummaryEntry, 'code'> {
  issueCode?: string;
  code?: string;
}

interface IssueExternalLink {
  id: string;
  organisationId: string;
  scanId: string;
  issueId: string;
  provider: 'github';
  externalIssueId: string;
  externalIssueUrl: string;
  externalStatus: ExternalIssueStatus | null;
  repositoryOwner: string | null;
  repositoryName: string | null;
  issueNumber: number | null;
  lastSyncedAt: string | null;
  linkedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface IssueExternalIntegrations {
  github: {
    tokenConfigured: boolean;
    canCreate: boolean;
    defaultRepository: string | null;
  };
}

interface IssueBundleResponse {
  summary: IssueSummaryEntryResponse[];
  instances: IssueInstance[];
  externalIntegrations?: IssueExternalIntegrations;
}

interface IssueInstance {
  id: string;
  scanPageId: string;
  pageUrl?: string | null;
  pageTitle?: string | null;
  selector: string | null;
  htmlSnippet: string | null;
  likelyCause: string;
  developerFixGuidance: string;
  pageEvidence: {
    id: string;
    recordType: string;
    createdAt: string;
  } | null;
  severityOverride: IssueImpact | null;
  workflowState: IssueWorkflowState;
  workflowAssigneeName: string | null;
  workflowAssigneeEmail: string | null;
  workflowNote: string | null;
  workflowUpdatedAt: string | null;
  firstSeenScanId: string;
  lastSeenScanId: string;
  suppression: IssueSuppression | null;
  issue: {
    code: string;
    title: string;
    impact: string;
    wcagCriteria: string;
    plainEnglishExplanation: string;
  };
}

interface ScanPage {
  id: string;
  url: string;
  title: string | null;
  status: string;
  httpStatusCode: number | null;
  failureReason: string | null;
  issueCount: number;
}

interface EvidenceRecord {
  id: string;
  scanPageId: string | null;
  issueInstanceId?: string | null;
  recordType: string;
  storagePath: string;
  fileSizeBytes: number;
  contentHash: string;
  createdAt: string;
}

interface ScanSummary {
  scan: {
    id: string;
    status: string;
    scanType?: string;
    startedAt: string | null;
    completedAt: string | null;
    failedAt: string | null;
    failureReason: string | null;
    pagesDiscovered: number;
    pagesScanned: number;
    pagesFailed: number;
    totalIssues: number;
    auth: {
      mode: 'public' | 'bearer_token' | 'session_storage_state' | 'form_login';
      blockedByAuthFailure: boolean;
      failureCategory: string | null;
      message: string | null;
      consoleHint: string | null;
      networkHint: string | null;
      refreshAttempted: boolean;
      refreshSucceeded: boolean;
      screenshotAvailable: boolean;
      diagnosticBundleAvailable: boolean;
      usedAuthenticatedMode: boolean;
      authenticatedPagesScanned: number;
      discoveredRouteCount: number;
    } | null;
  };
  site: {
    id: string;
    name: string;
    baseUrl: string;
  };
  issueCounts: { critical: number; serious: number; moderate: number; minor: number };
  workflowSummary: IssueWorkflowSummary;
  suppressedIssues: { total: number };
}

interface OrgUsageResponse {
  limits: {
    complianceExportEnabled: boolean;
  };
}

interface InlineSuppressionFeedback {
  instanceId: string;
  issueId?: string;
  message: string;
  detail?: string | null;
}

interface InlineWorkflowFeedback {
  issueId: string;
  message: string;
}

interface InlineExternalIssueFeedback {
  issueId: string;
  variant: 'success' | 'danger';
  message: string;
  detail?: string | null;
}

type Tab = 'issues' | 'pages' | 'evidence';

const IMPACT_ORDER = ['critical', 'serious', 'moderate', 'minor'];
const DEFAULT_EXTERNAL_INTEGRATIONS: IssueExternalIntegrations = {
  github: {
    tokenConfigured: false,
    canCreate: false,
    defaultRepository: null,
  },
};

// -------------------------------------------------------------------
// Design tokens
// -------------------------------------------------------------------

const C = {
  page: 'var(--acc-color-page)',
  text: 'var(--acc-color-text)',
  muted: 'var(--acc-color-text-muted)',
  soft: 'var(--acc-color-text-soft)',
  link: 'var(--acc-color-primary)',
  surface: 'var(--acc-color-surface)',
  surfaceMuted: 'var(--acc-color-surface-muted)',
  border: 'var(--acc-color-border)',
  danger: 'var(--acc-color-danger)',
  warning: 'var(--acc-color-warning)',
  success: 'var(--acc-color-success)',
  successSoft: 'var(--acc-color-success-soft)',
  dangerSoft: 'var(--acc-color-danger-soft)',
  warningSoft: 'var(--acc-color-warning-soft)',
  accentSoft: 'var(--acc-color-accent-soft)',
};

const tbl = {
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13, tableLayout: 'fixed' as const },
  th: {
    textAlign: 'left' as const,
    padding: '10px 16px',
    background: C.surfaceMuted,
    borderBottom: `1px solid ${C.border}`,
    color: C.soft,
    fontWeight: 600,
    fontSize: 11,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  td: {
    padding: '12px 16px',
    borderBottom: `1px solid ${C.border}`,
    verticalAlign: 'top' as const,
  },
};

// -------------------------------------------------------------------
// Status / impact → Badge variant
// -------------------------------------------------------------------

function statusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'completed':               return 'success';
    case 'completed_with_failures': return 'warning';
    case 'running':                 return 'warning';
    case 'queued':                  return 'info';
    case 'failed':                  return 'danger';
    case 'auth_failed':             return 'danger';
    default:                        return 'neutral';
  }
}

function impactVariant(impact: string): BadgeVariant {
  switch (impact) {
    case 'critical': return 'danger';
    case 'serious':  return 'warning';
    case 'moderate': return 'warning';
    case 'minor':    return 'neutral';
    default:         return 'neutral';
  }
}

function resolveIssueDeliveryStatus(state: IssueWorkflowState): IssueDeliveryStatus {
  switch (state) {
    case 'acknowledged':
    case 'in_progress':
      return 'in_progress';
    case 'resolved':
    case 'false_positive':
      return 'fixed';
    case 'open':
    default:
      return 'open';
  }
}

function formatIssueDeliveryStatus(status: IssueDeliveryStatus): string {
  switch (status) {
    case 'in_progress':
      return formatWorkflowState('in_progress');
    case 'fixed':
      return formatWorkflowState('resolved');
    case 'open':
    default:
      return formatWorkflowState('open');
  }
}

function issueDeliveryStatusVariant(status: IssueDeliveryStatus): BadgeVariant {
  switch (status) {
    case 'in_progress':
      return 'warning';
    case 'fixed':
      return 'success';
    case 'open':
    default:
      return 'info';
  }
}

function externalIssueStatusVariant(status: ExternalIssueStatus | null): BadgeVariant {
  switch (status) {
    case 'closed':
      return 'success';
    case 'open':
      return 'warning';
    case 'unknown':
    default:
      return 'neutral';
  }
}

function toPersistedWorkflowState(status: IssueDeliveryStatus): Extract<IssueWorkflowState, 'open' | 'in_progress' | 'resolved'> {
  switch (status) {
    case 'in_progress':
      return 'in_progress';
    case 'fixed':
      return 'resolved';
    case 'open':
    default:
      return 'open';
  }
}

function resolveReviewedSeverity(impact: IssueImpact, severityOverride: IssueImpact | null): IssueImpact {
  return severityOverride ?? impact;
}

function hasAutomatedFindingReview(entry: Pick<IssueSummaryEntry, 'workflowState' | 'severityOverride' | 'workflowNote'>): boolean {
  return entry.workflowState !== 'open'
    || entry.severityOverride !== null
    || Boolean(entry.workflowNote?.trim());
}

function createIssueReviewDraft(entry: Pick<IssueSummaryEntry, 'workflowState' | 'severityOverride' | 'workflowNote'>): IssueReviewDraft {
  return {
    workflowState: resolveIssueDeliveryStatus(entry.workflowState),
    severityOverride: entry.severityOverride ?? '',
    note: entry.workflowNote ?? '',
  };
}

// -------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------

function normalizeIssueSummaryEntry(entry: IssueSummaryEntryResponse): IssueSummaryEntry {
  return {
    ...entry,
    code: entry.code ?? entry.issueCode ?? 'unknown_issue',
    severityOverride: entry.severityOverride ?? null,
    externalLink: entry.externalLink ?? null,
  };
}

function formatWorkflowState(state: IssueWorkflowState): string {
  switch (state) {
    case 'acknowledged': return i18n.t('scanDetail:labels.workflowStates.acknowledged');
    case 'in_progress': return i18n.t('scanDetail:labels.workflowStates.inProgress');
    case 'resolved': return i18n.t('scanDetail:labels.workflowStates.resolved');
    case 'false_positive': return i18n.t('scanDetail:labels.workflowStates.falsePositive');
    case 'open':
    default:
      return i18n.t('scanDetail:labels.workflowStates.open');
  }
}

function formatExternalIssueStatus(status: ExternalIssueStatus | null): string {
  switch (status) {
    case 'closed':
      return i18n.t('scanDetail:labels.externalIssueStatuses.closed');
    case 'open':
      return i18n.t('scanDetail:labels.externalIssueStatuses.open');
    case 'unknown':
    default:
      return i18n.t('scanDetail:labels.externalIssueStatuses.unknown');
  }
}

function buildGitHubIssueLabel(link: IssueExternalLink): string {
  if (link.repositoryOwner && link.repositoryName && link.issueNumber) {
    return `${link.repositoryOwner}/${link.repositoryName} #${link.issueNumber}`;
  }

  return link.externalIssueUrl;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function formatAuthMode(mode: string): string {
  switch (mode) {
    case 'public': return i18n.t('scanDetail:labels.authModes.public');
    case 'bearer_token': return i18n.t('scanDetail:labels.authModes.bearerToken');
    case 'session_storage_state': return i18n.t('scanDetail:labels.authModes.sessionStorageState');
    case 'form_login': return i18n.t('scanDetail:labels.authModes.formLogin');
    default: return String(mode).replace(/_/g, ' ');
  }
}

function formatAuthFailureCategory(category: string | null): string | null {
  if (!category) return null;
  switch (category) {
    case 'session_expired': return i18n.t('scanDetail:labels.authFailures.sessionExpired');
    case 'redirected_to_login_mid_scan': return i18n.t('scanDetail:labels.authFailures.redirectedToLoginMidScan');
    case 'auth_session_invalidated': return i18n.t('scanDetail:labels.authFailures.authSessionInvalidated');
    case 'redirected_to_login': return i18n.t('scanDetail:labels.authFailures.redirectedToLogin');
    case 'selector_missing': return i18n.t('scanDetail:labels.authFailures.selectorMissing');
    case 'success_selector_not_found': return i18n.t('scanDetail:labels.authFailures.successSelectorNotFound');
    case 'success_url_pattern_not_matched': return i18n.t('scanDetail:labels.authFailures.successUrlPatternNotMatched');
    case 'selector_not_found': return i18n.t('scanDetail:labels.authFailures.selectorNotFound');
    case 'auth_token_rejected': return i18n.t('scanDetail:labels.authFailures.authTokenRejected');
    default: return category.replace(/_/g, ' ');
  }
}

function formatImpactLabel(impact: string): string {
  switch (impact) {
    case 'critical':
      return i18n.t('scanDetail:labels.severity.critical');
    case 'serious':
      return i18n.t('scanDetail:labels.severity.serious');
    case 'moderate':
      return i18n.t('scanDetail:labels.severity.moderate');
    case 'minor':
      return i18n.t('scanDetail:labels.severity.minor');
    default:
      return impact.replace(/_/g, ' ');
  }
}

function formatScanStatus(status: string): string {
  switch (status) {
    case 'completed':
      return i18n.t('scanDetail:labels.scanStatuses.completed');
    case 'completed_with_failures':
      return i18n.t('scanDetail:labels.scanStatuses.completedWithFailures');
    case 'running':
      return i18n.t('scanDetail:labels.scanStatuses.running');
    case 'queued':
      return i18n.t('scanDetail:labels.scanStatuses.queued');
    case 'failed':
      return i18n.t('scanDetail:labels.scanStatuses.failed');
    case 'auth_failed':
      return i18n.t('scanDetail:labels.scanStatuses.authFailed');
    default:
      return status.replace(/_/g, ' ');
  }
}

function formatEvidenceType(recordType: string): string {
  switch (recordType) {
    case 'screenshot':
      return i18n.t('scanDetail:labels.evidenceTypes.screenshot');
    case 'html_snapshot':
      return i18n.t('scanDetail:labels.evidenceTypes.htmlSnapshot');
    case 'dom_snapshot':
      return i18n.t('scanDetail:labels.evidenceTypes.domSnapshot');
    case 'console_log':
      return i18n.t('scanDetail:labels.evidenceTypes.consoleLog');
    case 'network_log':
      return i18n.t('scanDetail:labels.evidenceTypes.networkLog');
    case 'auth_screenshot':
      return i18n.t('scanDetail:labels.evidenceTypes.authScreenshot');
    case 'auth_diagnostics_bundle':
      return i18n.t('scanDetail:labels.evidenceTypes.authDiagnosticsBundle');
    default:
      return recordType.replace(/_/g, ' ');
  }
}

function toDateInputValue(value: string | null): string {
  if (!value) return '';
  return new Date(value).toISOString().slice(0, 10);
}

function escapeCsvCell(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
}

function buildIssueExportCsv(entries: IssueSummaryEntry[]): string {
  const headers = [
    'title',
    'severity',
    'url',
    'wcag reference',
    'description',
    'suggested fix',
  ];

  const rows = entries.map((entry) => [
    escapeCsvCell(entry.title),
    escapeCsvCell(formatImpactLabel(entry.impact)),
    escapeCsvCell(entry.samplePageUrl ?? ''),
    escapeCsvCell(`WCAG ${entry.wcagLevel} ${entry.wcagCriteria}`),
    escapeCsvCell(entry.plainEnglishExplanation),
    escapeCsvCell(entry.developerFixGuidance || entry.remediationGuidance),
  ]);

  return [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
}

function buildIssueJiraCopy(entry: IssueSummaryEntry): string {
  const suggestedFix = entry.developerFixGuidance?.trim() || entry.remediationGuidance?.trim();

  return [
    `Title: [${formatImpactLabel(entry.impact)}] ${entry.title}`,
    `Severity: ${formatImpactLabel(entry.impact)}`,
    `Status: ${formatIssueDeliveryStatus(resolveIssueDeliveryStatus(entry.workflowState))}`,
    `URL: ${entry.samplePageUrl ?? 'No sample URL recorded'}`,
    `WCAG reference: WCAG ${entry.wcagLevel} ${entry.wcagCriteria}`,
    '',
    'Description:',
    entry.plainEnglishExplanation,
    '',
    'Suggested fix:',
    suggestedFix || 'Review the affected markup and apply the recommended accessibility remediation.',
  ].join('\n');
}

// -------------------------------------------------------------------
// Page component
// -------------------------------------------------------------------

export default function ScanDetailPage() {
  const { t } = useTranslation(['scanDetail', 'common']);
  const { id } = useParams<{ id: string }>();
  const { role } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('issues');
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<'all' | 'critical' | 'serious' | 'moderate' | 'minor'>('all');
  const [workflowFilter, setWorkflowFilter] = useState<'all' | IssueDeliveryStatus>('all');

  const [summary, setSummary] = useState<ScanSummary | null>(null);
  const [issueInstances, setIssueInstances] = useState<IssueInstance[]>([]);
  const [issueSummary, setIssueSummary] = useState<IssueSummaryEntry[]>([]);
  const [externalIntegrations, setExternalIntegrations] = useState<IssueExternalIntegrations>(DEFAULT_EXTERNAL_INTEGRATIONS);
  const [pages, setPages] = useState<ScanPage[]>([]);
  const [evidence, setEvidence] = useState<EvidenceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [authDiagnosticError, setAuthDiagnosticError] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewSuccess, setReviewSuccess] = useState<InlineWorkflowFeedback | null>(null);
  const [suppressionError, setSuppressionError] = useState<InlineSuppressionFeedback | null>(null);
  const [suppressionSuccess, setSuppressionSuccess] = useState<InlineSuppressionFeedback | null>(null);
  const [usageData, setUsageData] = useState<OrgUsageResponse | null>(null);
  const [showExportUpgradeModal, setShowExportUpgradeModal] = useState(false);
  const [downloadingAuthBundle, setDownloadingAuthBundle] = useState(false);
  const [openingAuthScreenshot, setOpeningAuthScreenshot] = useState(false);
  const [reviewModalIssueId, setReviewModalIssueId] = useState<string | null>(null);
  const [reviewSavingIssueId, setReviewSavingIssueId] = useState<string | null>(null);
  const [reviewDrafts, setReviewDrafts] = useState<Record<string, IssueReviewDraft>>({});
  const [openOccurrenceIssueIds, setOpenOccurrenceIssueIds] = useState<string[]>([]);
  const [suppressionEditorInstanceId, setSuppressionEditorInstanceId] = useState<string | null>(null);
  const [suppressionReason, setSuppressionReason] = useState('');
  const [suppressionExpiry, setSuppressionExpiry] = useState('');
  const [suppressionScope, setSuppressionScope] = useState<'page' | 'selector'>('page');
  const [suppressionBusyInstanceId, setSuppressionBusyInstanceId] = useState<string | null>(null);
  const [jiraCopyState, setJiraCopyState] = useState<{
    issueId: string;
    state: 'copying' | 'copied' | 'failed';
  } | null>(null);
  const [externalIssueBusy, setExternalIssueBusy] = useState<{
    issueId: string;
    action: 'create' | 'link' | 'sync';
  } | null>(null);
  const [externalIssueFeedback, setExternalIssueFeedback] = useState<InlineExternalIssueFeedback | null>(null);
  const [externalLinkEditorIssueId, setExternalLinkEditorIssueId] = useState<string | null>(null);
  const [externalIssueDrafts, setExternalIssueDrafts] = useState<Record<string, string>>({});
  const reviewSuccessTimerRef = useRef<number | null>(null);
  const suppressionErrorRef = useRef<HTMLDivElement | null>(null);
  const suppressionSuccessRef = useRef<HTMLDivElement | null>(null);
  const suppressionSuccessTimerRef = useRef<number | null>(null);
  const jiraCopyTimerRef = useRef<number | null>(null);

  const setOccurrenceDetailOpen = useCallback((issueId: string, isOpen: boolean) => {
    setOpenOccurrenceIssueIds((current) => {
      if (isOpen) {
        return current.includes(issueId) ? current : [...current, issueId];
      }
      return current.filter((value) => value !== issueId);
    });
  }, []);

  const applyIssueBundle = useCallback((bundle: IssueBundleResponse) => {
    setIssueSummary((bundle.summary ?? []).map(normalizeIssueSummaryEntry));
    setIssueInstances(bundle.instances ?? []);
    setExternalIntegrations(bundle.externalIntegrations ?? DEFAULT_EXTERNAL_INTEGRATIONS);
  }, []);

  const loadIssueBundle = useCallback(async (): Promise<IssueBundleResponse> => {
    if (!id) {
      return {
        summary: [],
        instances: [],
        externalIntegrations: DEFAULT_EXTERNAL_INTEGRATIONS,
      };
    }

    return api.get<IssueBundleResponse>(`/scans/${id}/issues`);
  }, [id]);

  const refreshIssueBundle = useCallback(async () => {
    const bundle = await loadIssueBundle();
    applyIssueBundle(bundle);
  }, [applyIssueBundle, loadIssueBundle]);

  const loadData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      setAuthDiagnosticError(null);

      const [summaryRes, issuesRes, pagesRes, evidenceRes, usageRes] = await Promise.allSettled([
        api.get<ScanSummary>(`/reports/scans/${id}/summary`),
        loadIssueBundle(),
        api.get<{ pages: ScanPage[] }>(`/scans/${id}/pages`),
        api.get<{ evidence: EvidenceRecord[] }>(`/scans/${id}/evidence`),
        api.get<OrgUsageResponse>('/orgs/me/usage'),
      ]);

      // Summary is the primary resource — if it failed (403/404/etc.), surface immediately.
      if (summaryRes.status === 'rejected') {
        setError(friendlyError(summaryRes.reason, t('feedback.loadFailed')));
        return;
      }
      setSummary(summaryRes.value);

      if (issuesRes.status === 'fulfilled') {
        applyIssueBundle(issuesRes.value);
      }
      if (pagesRes.status === 'fulfilled') setPages(pagesRes.value.pages ?? []);
      if (evidenceRes.status === 'fulfilled') setEvidence(evidenceRes.value.evidence ?? []);
      if (usageRes.status === 'fulfilled') setUsageData(usageRes.value);
    } catch (err) {
      setError(friendlyError(err, t('feedback.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [applyIssueBundle, id, loadIssueBundle, t]);

  const refreshReviewData = useCallback(async () => {
    if (!id) {
      return;
    }

    const [summaryResponse, bundle] = await Promise.all([
      api.get<ScanSummary>(`/reports/scans/${id}/summary`),
      loadIssueBundle(),
    ]);

    setSummary(summaryResponse);
    applyIssueBundle(bundle);
  }, [applyIssueBundle, id, loadIssueBundle]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    return () => {
      if (reviewSuccessTimerRef.current !== null) {
        window.clearTimeout(reviewSuccessTimerRef.current);
      }
      if (suppressionSuccessTimerRef.current !== null) {
        window.clearTimeout(suppressionSuccessTimerRef.current);
      }
      if (jiraCopyTimerRef.current !== null) {
        window.clearTimeout(jiraCopyTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!suppressionError || suppressionEditorInstanceId !== suppressionError.instanceId) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      suppressionErrorRef.current?.focus({ preventScroll: true });
      suppressionErrorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [suppressionError, suppressionEditorInstanceId]);

  useEffect(() => {
    if (!suppressionSuccess || suppressionEditorInstanceId === suppressionSuccess.instanceId) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      suppressionSuccessRef.current?.focus({ preventScroll: true });
      suppressionSuccessRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [suppressionEditorInstanceId, suppressionSuccess]);

  const handleOpenEvidence = async (evidenceId: string) => {
    setEvidenceError(null);
    try {
      const response = await fetchAuthenticated(`/evidence/${evidenceId}/file`);
      if (!response.ok) {
        const msg =
          response.status === 403 ? t('feedback.evidenceAccessDenied') :
          response.status === 404 ? t('feedback.evidenceNotFound') :
          t('feedback.evidenceOpenFailed', { status: response.status });
        setEvidenceError(msg);
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error('Failed to open evidence file', err);
      setEvidenceError(t('feedback.evidenceLoadFailed'));
    }
  };

  const handleExportJson = async () => {
    setExportError(null);
    try {
      const response = await fetchAuthenticated(`/reports/scans/${id}/export`);
      if (response.status === 403) {
        setShowExportUpgradeModal(true);
        return;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = response.headers.get('Content-Disposition');
      const match = cd?.match(/filename="([^"]+)"/);
      a.download = match?.[1] ?? `ablecomply-scan-${id?.slice(0, 8)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error('Failed to export scan JSON', err);
      setExportError(t('feedback.exportFailed'));
    }
  };

  const canManageAuthDiagnostics = role === 'owner' || role === 'admin';
  const canManageSuppressions = role === 'owner' || role === 'admin';
  const canReviewFindings = role !== null;

  const getReviewDraft = (entry: IssueSummaryEntry): IssueReviewDraft =>
    reviewDrafts[entry.issueId] ?? createIssueReviewDraft(entry);

  const updateReviewDraft = (
    issueId: string,
    patch: Partial<IssueReviewDraft>,
  ) => {
    setReviewDrafts((current) => {
      const existing = current[issueId] ?? {
        workflowState: 'open' as const,
        severityOverride: '',
        note: '',
      };
      return {
        ...current,
        [issueId]: {
          ...existing,
          ...patch,
        },
      };
    });
  };

  const openIssueReviewModal = (entry: IssueSummaryEntry) => {
    setReviewError(null);
    setReviewDrafts((current) => ({
      ...current,
      [entry.issueId]: createIssueReviewDraft(entry),
    }));
    setReviewModalIssueId(entry.issueId);
  };

  const closeIssueReviewModal = () => {
    setReviewModalIssueId(null);
    setReviewError(null);
  };

  const handleSaveReview = async (entry: IssueSummaryEntry) => {
    if (!id) return;

    const draft = getReviewDraft(entry);
    setReviewSavingIssueId(entry.issueId);
    setReviewError(null);
    setReviewSuccess(null);
    try {
      await api.patch(`/scans/${id}/issues/${entry.issueId}/workflow`, {
        workflowState: toPersistedWorkflowState(draft.workflowState),
        severityOverride: draft.severityOverride || null,
        note: draft.note.trim() || null,
      });
      await refreshReviewData();
      setReviewModalIssueId(null);
      setReviewDrafts((current) => {
        const next = { ...current };
        delete next[entry.issueId];
        return next;
      });
      if (reviewSuccessTimerRef.current !== null) {
        window.clearTimeout(reviewSuccessTimerRef.current);
      }
      setReviewSuccess({
        issueId: entry.issueId,
        message: t('feedback.reviewSaved'),
      });
      reviewSuccessTimerRef.current = window.setTimeout(() => {
        setReviewSuccess((current) => current?.issueId === entry.issueId ? null : current);
        reviewSuccessTimerRef.current = null;
      }, 4000);
    } catch (err) {
      setReviewError(friendlyError(err, t('feedback.reviewSaveFailed')));
    } finally {
      setReviewSavingIssueId(null);
    }
  };

  const handleCopyIssueForJira = async (entry: IssueSummaryEntry) => {
    if (jiraCopyTimerRef.current !== null) {
      window.clearTimeout(jiraCopyTimerRef.current);
      jiraCopyTimerRef.current = null;
    }

    setJiraCopyState({ issueId: entry.issueId, state: 'copying' });
    const copied = await copyTextToClipboard(buildIssueJiraCopy(entry));
    setJiraCopyState({ issueId: entry.issueId, state: copied ? 'copied' : 'failed' });
    jiraCopyTimerRef.current = window.setTimeout(() => {
      setJiraCopyState((current) => current?.issueId === entry.issueId ? null : current);
      jiraCopyTimerRef.current = null;
    }, 2500);
  };

  const updateExternalIssueDraft = (issueId: string, nextValue: string) => {
    setExternalIssueDrafts((current) => ({
      ...current,
      [issueId]: nextValue,
    }));
  };

  const setExternalIssueResult = (
    issueId: string,
    variant: InlineExternalIssueFeedback['variant'],
    message: string,
    detail?: string | null,
  ) => {
    setExternalIssueFeedback({
      issueId,
      variant,
      message,
      detail: detail ?? null,
    });
  };

  const extractExternalIssueDetail = (err: unknown, fallback: string): string | null => {
    const detail = friendlyError(err, fallback);
    return detail !== fallback ? detail : null;
  };

  const handleCreateGitHubIssue = async (entry: IssueSummaryEntry) => {
    if (!id) return;

    setExternalIssueBusy({ issueId: entry.issueId, action: 'create' });
    setExternalIssueFeedback(null);
    try {
      await api.post(`/scans/${id}/issues/${entry.issueId}/external/github/create`);
      await refreshIssueBundle();
      setExternalLinkEditorIssueId(null);
      setExternalIssueResult(entry.issueId, 'success', t('feedback.githubIssueCreated'));
    } catch (err) {
      setExternalIssueResult(
        entry.issueId,
        'danger',
        t('feedback.githubIssueCreateFailed'),
        extractExternalIssueDetail(err, t('feedback.githubIssueCreateFailed')),
      );
    } finally {
      setExternalIssueBusy(null);
    }
  };

  const handleLinkGitHubIssue = async (entry: IssueSummaryEntry) => {
    if (!id) return;

    const issueUrl = (externalIssueDrafts[entry.issueId] ?? '').trim();
    setExternalIssueBusy({ issueId: entry.issueId, action: 'link' });
    setExternalIssueFeedback(null);
    try {
      await api.post(`/scans/${id}/issues/${entry.issueId}/external/github/link`, {
        issueUrl,
      });
      await refreshIssueBundle();
      setExternalLinkEditorIssueId(null);
      updateExternalIssueDraft(entry.issueId, '');
      setExternalIssueResult(entry.issueId, 'success', t('feedback.githubIssueLinked'));
    } catch (err) {
      setExternalIssueResult(
        entry.issueId,
        'danger',
        t('feedback.githubIssueLinkFailed'),
        extractExternalIssueDetail(err, t('feedback.githubIssueLinkFailed')),
      );
    } finally {
      setExternalIssueBusy(null);
    }
  };

  const handleSyncGitHubIssue = async (entry: IssueSummaryEntry) => {
    if (!id) return;

    setExternalIssueBusy({ issueId: entry.issueId, action: 'sync' });
    setExternalIssueFeedback(null);
    try {
      await api.post(`/scans/${id}/issues/${entry.issueId}/external/sync`);
      await refreshIssueBundle();
      setExternalIssueResult(entry.issueId, 'success', t('feedback.githubStatusSynced'));
    } catch (err) {
      setExternalIssueResult(
        entry.issueId,
        'danger',
        t('feedback.githubStatusSyncFailed'),
        extractExternalIssueDetail(err, t('feedback.githubStatusSyncFailed')),
      );
    } finally {
      setExternalIssueBusy(null);
    }
  };

  const openSuppressionEditor = (instance: IssueInstance, issueId: string) => {
    if (suppressionSuccessTimerRef.current !== null) {
      window.clearTimeout(suppressionSuccessTimerRef.current);
      suppressionSuccessTimerRef.current = null;
    }
    setOccurrenceDetailOpen(issueId, true);
    setSuppressionEditorInstanceId(instance.id);
    setSuppressionReason(instance.suppression?.reason ?? '');
    setSuppressionExpiry(toDateInputValue(instance.suppression?.expiresAt ?? null));
    setSuppressionScope(instance.selector ? 'selector' : 'page');
    setSuppressionError(null);
    setSuppressionSuccess(null);
  };

  const resetSuppressionEditor = () => {
    setSuppressionEditorInstanceId(null);
    setSuppressionReason('');
    setSuppressionExpiry('');
    setSuppressionScope('page');
    setSuppressionError(null);
  };

  const handleSaveSuppression = async (instance: IssueInstance, issueId: string) => {
    if (!id) return;
    setOccurrenceDetailOpen(issueId, true);
    setSuppressionBusyInstanceId(instance.id);
    setSuppressionError(null);
    setSuppressionSuccess(null);
    try {
      await api.post(`/scans/${id}/issues/${instance.id}/suppression`, {
        reason: suppressionReason.trim(),
        expiresAt: suppressionExpiry
          ? new Date(`${suppressionExpiry}T00:00:00`).toISOString()
          : null,
        scope: suppressionScope,
      });
      resetSuppressionEditor();
      await loadData();
      setOccurrenceDetailOpen(issueId, true);
      if (suppressionSuccessTimerRef.current !== null) {
        window.clearTimeout(suppressionSuccessTimerRef.current);
      }
      setSuppressionSuccess({
        instanceId: instance.id,
        issueId,
        message: t('feedback.suppressionSaved'),
      });
      suppressionSuccessTimerRef.current = window.setTimeout(() => {
        setSuppressionSuccess((current) => current?.instanceId === instance.id ? null : current);
        suppressionSuccessTimerRef.current = null;
      }, 3000);
    } catch (err) {
      console.error('Failed to save issue suppression', err);
      const detail = err instanceof ApiError
        ? friendlyError(err, err.message || t('feedback.suppressionSaveFailed'))
        : err instanceof Error
          ? err.message
          : null;
      setSuppressionError({
        instanceId: instance.id,
        message: t('feedback.suppressionSaveFailed'),
        detail: detail && detail !== t('feedback.suppressionSaveFailed') ? detail : null,
      });
    } finally {
      setSuppressionBusyInstanceId(null);
    }
  };

  const handleClearSuppression = async (instance: IssueInstance, issueId: string) => {
    if (!id) return;
    setOccurrenceDetailOpen(issueId, true);
    setSuppressionBusyInstanceId(instance.id);
    setSuppressionError(null);
    setSuppressionSuccess(null);
    try {
      await api.delete(`/scans/${id}/issues/${instance.id}/suppression`);
      resetSuppressionEditor();
      await loadData();
      setOccurrenceDetailOpen(issueId, true);
      if (suppressionSuccessTimerRef.current !== null) {
        window.clearTimeout(suppressionSuccessTimerRef.current);
      }
      setSuppressionSuccess({
        instanceId: instance.id,
        issueId,
        message: t('feedback.suppressionCleared'),
      });
      suppressionSuccessTimerRef.current = window.setTimeout(() => {
        setSuppressionSuccess((current) => current?.instanceId === instance.id ? null : current);
        suppressionSuccessTimerRef.current = null;
      }, 3000);
    } catch (err) {
      console.error('Failed to clear issue suppression', err);
      const detail = err instanceof ApiError
        ? friendlyError(err, err.message || t('feedback.suppressionClearFailed'))
        : err instanceof Error
          ? err.message
          : null;
      setSuppressionError({
        instanceId: instance.id,
        message: t('feedback.suppressionClearFailed'),
        detail: detail && detail !== t('feedback.suppressionClearFailed') ? detail : null,
      });
    } finally {
      setSuppressionBusyInstanceId(null);
    }
  };

  const handleDownloadAuthDiagnostics = async () => {
    if (!id) return;
    setDownloadingAuthBundle(true);
    setAuthDiagnosticError(null);
    try {
      const response = await fetchAuthenticated(`/scans/${id}/auth-diagnostics/bundle`);
      if (!response.ok) {
        const message =
          response.status === 403 ? t('feedback.authDiagnosticsAccessDenied') :
          response.status === 404 ? t('feedback.authDiagnosticsNotFound') :
          t('feedback.authDiagnosticsDownloadFailed', { status: response.status });
        setAuthDiagnosticError(message);
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = response.headers.get('Content-Disposition');
      const match = cd?.match(/filename="([^"]+)"/);
      a.download = match?.[1] ?? `scan-auth-diagnostics-${id}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error('Failed to download authenticated scan diagnostics', err);
      setAuthDiagnosticError(t('feedback.authDiagnosticsDownloadRetry'));
    } finally {
      setDownloadingAuthBundle(false);
    }
  };

  const handleOpenAuthScreenshot = async () => {
    if (!id) return;
    setOpeningAuthScreenshot(true);
    setAuthDiagnosticError(null);
    try {
      const response = await fetchAuthenticated(`/scans/${id}/auth-diagnostics/screenshot`);
      if (!response.ok) {
        const message =
          response.status === 403 ? t('feedback.authScreenshotAccessDenied') :
          response.status === 404 ? t('feedback.authScreenshotNotFound') :
          t('feedback.authScreenshotOpenFailed', { status: response.status });
        setAuthDiagnosticError(message);
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error('Failed to open authenticated scan screenshot', err);
      setAuthDiagnosticError(t('feedback.authScreenshotOpenRetry'));
    } finally {
      setOpeningAuthScreenshot(false);
    }
  };

  // -------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------

  const issueCounts = summary?.issueCounts ?? { critical: 0, serious: 0, moderate: 0, minor: 0 };
  const scanAuth = summary?.scan.auth ?? null;
  const scanFailurePresentation = useMemo(() => {
    if (!summary || summary.scan.status !== 'failed' || !summary.scan.failureReason) {
      return null;
    }

    return resolveScanFailurePresentation(t, {
      status: summary.scan.status,
      failureReason: summary.scan.failureReason,
    });
  }, [summary, t]);
  const sortedIssueSummary = [...issueSummary].sort(
    (a, b) => IMPACT_ORDER.indexOf(a.impact) - IMPACT_ORDER.indexOf(b.impact)
  );
  const filteredIssueSummary = useMemo(() => {
    return sortedIssueSummary.filter((entry) => {
      if (severityFilter !== 'all' && entry.impact !== severityFilter) {
        return false;
      }
      if (
        workflowFilter !== 'all' &&
        resolveIssueDeliveryStatus(entry.workflowState) !== workflowFilter
      ) {
        return false;
      }
      return true;
    });
  }, [severityFilter, sortedIssueSummary, workflowFilter]);
  const issueStatusSummary = useMemo(() => {
    return sortedIssueSummary.reduce(
      (summaryAccumulator, entry) => {
        const status = resolveIssueDeliveryStatus(entry.workflowState);
        if (status === 'in_progress') {
          summaryAccumulator.inProgress += 1;
        } else if (status === 'fixed') {
          summaryAccumulator.fixed += 1;
        } else {
          summaryAccumulator.open += 1;
        }

        if (entry.suppressedCount > 0) {
          summaryAccumulator.suppressed += 1;
        }

        return summaryAccumulator;
      },
      {
        open: 0,
        inProgress: 0,
        fixed: 0,
        suppressed: 0,
      },
    );
  }, [sortedIssueSummary]);
  const selectedIssueEntry = useMemo(() => {
    if (filteredIssueSummary.length === 0) {
      return null;
    }
    return filteredIssueSummary.find((entry) => entry.issueId === selectedIssueId) ?? filteredIssueSummary[0];
  }, [filteredIssueSummary, selectedIssueId]);
  const activeReviewEntry = useMemo(
    () => issueSummary.find((entry) => entry.issueId === reviewModalIssueId) ?? null,
    [issueSummary, reviewModalIssueId],
  );

  // Full score — all four severity counts available from summary endpoint
  const scanScore = summary
    ? calculateAccessibilityScore(issueCounts)
    : null;
  const scoreColor = scanScore !== null ? getScoreColor(scanScore) : C.muted;

  // Priority findings: top critical then serious entries
  const priorityFindings = sortedIssueSummary
    .filter((e) => e.impact === 'critical' || e.impact === 'serious')
    .slice(0, 5);

  const severityStrip = [
    { key: 'critical', label: t('labels.severity.critical'), count: issueCounts.critical, color: C.danger, bg: C.dangerSoft },
    { key: 'serious',  label: t('labels.severity.serious'),  count: issueCounts.serious,  color: C.warning, bg: C.warningSoft },
    { key: 'moderate', label: t('labels.severity.moderate'), count: issueCounts.moderate, color: C.warning, bg: C.warningSoft },
    { key: 'minor',    label: t('labels.severity.minor'),    count: issueCounts.minor,    color: C.soft, bg: C.surfaceMuted },
  ];
  const complianceExportEnabled = usageData?.limits.complianceExportEnabled !== false;
  const complianceExportUpgradePath = buildBillingUpgradePath('growth', 'compliance-export-lock');
  const authenticatedPageCoverage = scanAuth && scanAuth.mode !== 'public'
    ? Math.max(scanAuth.authenticatedPagesScanned, summary?.scan.pagesScanned ?? 0)
    : 0;
  const authenticatedRouteCoverage = scanAuth && scanAuth.mode !== 'public'
    ? Math.max(scanAuth.discoveredRouteCount, summary?.scan.pagesDiscovered ?? 0)
    : 0;
  const canViewInternalReview = role === 'owner' || role === 'admin';
  const scanReview = useMemo(() => {
    if (!summary) {
      return null;
    }

    return deriveScanReview({
      status: summary.scan.status,
      pagesDiscovered: summary.scan.pagesDiscovered,
      pagesScanned: summary.scan.pagesScanned,
      pagesFailed: summary.scan.pagesFailed,
      totalIssues: summary.scan.totalIssues,
      severityCounts: issueCounts,
      auth: summary.scan.auth,
      issueTypes: issueSummary.map((entry) => ({
        impact: entry.impact,
        occurrenceCount: entry.occurrenceCount,
        affectedPages: entry.affectedPages,
        samplePageUrl: entry.samplePageUrl,
        samplePageEvidenceId: entry.samplePageEvidenceId,
        remediationGuidance: entry.remediationGuidance,
        likelyCause: entry.likelyCause,
        developerFixGuidance: entry.developerFixGuidance,
      })),
      issueInstances: issueInstances.map((instance) => ({
        pageUrl: instance.pageUrl,
        selector: instance.selector,
        htmlSnippet: instance.htmlSnippet,
        pageEvidence: instance.pageEvidence,
      })),
    });
  }, [issueCounts, issueInstances, issueSummary, summary]);

  useEffect(() => {
    if (filteredIssueSummary.length === 0) {
      setSelectedIssueId(null);
      return;
    }

    if (!selectedIssueId || !filteredIssueSummary.some((entry) => entry.issueId === selectedIssueId)) {
      setSelectedIssueId(filteredIssueSummary[0].issueId);
    }
  }, [filteredIssueSummary, selectedIssueId]);

  const handleExportIssuesCsv = () => {
    if (!id || filteredIssueSummary.length === 0) {
      return;
    }

    setExportError(null);
    try {
      const csv = buildIssueExportCsv(filteredIssueSummary);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `scan-${id}-issues.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (nextError) {
      console.error('Failed to export issues CSV', nextError);
      setExportError(t('feedback.issueExportFailed'));
    }
  };

  // -------------------------------------------------------------------
  // Loading state
  // -------------------------------------------------------------------

  if (loading) {
    return (
      <Container maxWidth={1480}>
          <p style={{ color: C.muted }}>{t('labels.loadingScan')}</p>
      </Container>
    );
  }

  // -------------------------------------------------------------------
  // Error / not found
  // -------------------------------------------------------------------

  if (error) {
    return (
      <Container maxWidth={1480}>
          <Card>
            <p style={{ color: C.danger, marginBottom: 16 }}>{error}</p>
            <Button variant="secondary" onClick={() => window.history.back()}>
              {t('actions.goBack')}
            </Button>
          </Card>
      </Container>
    );
  }

  // -------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------

  return (
    <Container maxWidth={1480}>

        {/* ── Breadcrumb ─────────────────────────────────────────── */}
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
          <Link to="/sites" style={{ color: C.link, textDecoration: 'none' }}>{t('common:navigation.sites')}</Link>
          {' › '}
          {summary ? (
            <>
              <Link to={`/sites/${summary.site.id}`} style={{ color: C.link, textDecoration: 'none' }}>
                {summary.site.name}
              </Link>
              {' › '}
            </>
          ) : null}
          <span>{t('header.eyebrow')}</span>
        </div>

        {/* ── Page header ────────────────────────────────────────── */}
        <div style={{ marginBottom: summary?.scan.status === 'failed' || summary?.scan.status === 'auth_failed' ? 16 : 24 }}>
          <PageHeader
            eyebrow={t('header.eyebrow')}
            title={summary ? t('header.title', { site: summary.site.name }) : t('header.eyebrow')}
            badge={
              summary ? (
                <Badge variant={statusVariant(summary.scan.status)}>
                  {formatScanStatus(summary.scan.status)}
                </Badge>
              ) : undefined
            }
            subtitle={
              summary ? (
                <span style={{ color: C.muted }}>
                  {summary.site.baseUrl}
                  {summary.scan.completedAt || summary.scan.failedAt ? ' · ' : ''}
                  {summary.scan.completedAt && t('labels.completedAt', { value: formatDateTime(summary.scan.completedAt) })}
                  {summary.scan.failedAt && t('labels.failedAt', { value: formatDateTime(summary.scan.failedAt) })}
                </span>
              ) : undefined
            }
            description={t('header.description')}
            meta={
              summary ? (
                <>
                  <Badge variant={summary.scan.auth?.mode === 'public' ? 'neutral' : 'info'}>
                    {formatAuthMode(summary.scan.auth?.mode ?? 'public')}
                  </Badge>
                  <Badge variant={summary.scan.totalIssues > 0 ? 'warning' : 'success'}>
                    {t('labels.issuesRecorded', { count: summary.scan.totalIssues })}
                  </Badge>
                  <Badge variant="neutral">
                    {t('labels.pagesScanned', { count: summary.scan.pagesScanned })}
                  </Badge>
                </>
              ) : undefined
            }
            action={
              <>
                {summary && (
                  <Link
                    to={`/sites/${summary.site.id}/compliance`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 40,
                      padding: '9px 16px',
                      borderRadius: 10,
                      border: `1px solid ${C.border}`,
                      background: C.surface,
                      color: C.text,
                      textDecoration: 'none',
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    {t('actions.complianceReport')}
                  </Link>
                )}
                {summary && (
                  <Link
                    to={`/sites/${summary.site.id}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 40,
                      padding: '9px 16px',
                      borderRadius: 10,
                      border: `1px solid ${C.border}`,
                      background: C.surface,
                      color: C.text,
                      textDecoration: 'none',
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    {t('actions.siteWorkspace')}
                  </Link>
                )}
                <Button variant="secondary" onClick={complianceExportEnabled ? handleExportJson : () => setShowExportUpgradeModal(true)}>
                  {complianceExportEnabled ? `↓ ${t('actions.export')}` : `🔒 ${t('actions.export')}`}
                </Button>
              </>
            }
          />
        </div>

        {/* ── Failure banner ─────────────────────────────────────── */}
        {summary?.scan.status === 'failed' && scanFailurePresentation && (
          <AlertBanner variant="danger" icon="✕" style={{ marginBottom: 24 }}>
            <div style={{ display: 'grid', gap: 6 }}>
              <strong>{scanFailurePresentation.title}</strong>
              <span>{scanFailurePresentation.body}</span>
              {scanFailurePresentation.nextStep && (
                <span style={{ color: C.muted }}>
                  {scanFailurePresentation.nextStep}
                </span>
              )}
            </div>
          </AlertBanner>
        )}

        {summary?.scan.status === 'auth_failed' && summary.scan.failureReason && (
          <AlertBanner variant="danger" icon="✕" style={{ marginBottom: 24 }}>
            <strong>{t('labels.authScanFailed')}</strong> {summary.scan.failureReason}
          </AlertBanner>
        )}

        {/* ── Partial failure banner (completed_with_failures) ───── */}
        {summary?.scan.status === 'completed_with_failures' && (
          <AlertBanner variant="warning" icon="⚠" style={{ marginBottom: 24 }}>
            <strong>{t('labels.partialScan')}</strong>{' '}
            {summary.scan.pagesFailed > 0
              ? t('labels.partialPagesFailed', { failed: summary.scan.pagesFailed, total: summary.scan.pagesScanned })
              : ''}
            {summary.scan.failureReason ?? t('labels.partialScanFallback')}
          </AlertBanner>
        )}

        {exportError && (
          <AlertBanner variant="danger" style={{ marginBottom: 24 }}>
            {exportError}
          </AlertBanner>
        )}

        {authDiagnosticError && (
          <AlertBanner variant="danger" style={{ marginBottom: 24 }}>
            {authDiagnosticError}
          </AlertBanner>
        )}

        {scanAuth && (
          <Card style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('labels.authAccessMode')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Badge variant={scanAuth.mode === 'public' ? 'neutral' : scanAuth.blockedByAuthFailure ? 'danger' : 'info'}>
                    {formatAuthMode(scanAuth.mode)}
                  </Badge>
                  {scanAuth.refreshAttempted && (
                    <Badge variant={scanAuth.refreshSucceeded ? 'success' : 'warning'}>
                      {scanAuth.refreshSucceeded ? t('labels.authRefreshRecovered') : t('labels.authRefreshAttempted')}
                    </Badge>
                  )}
                </div>
              </div>

              {scanAuth.mode !== 'public' && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {canManageAuthDiagnostics && scanAuth.diagnosticBundleAvailable && (
                    <Button type="button" variant="secondary" onClick={handleDownloadAuthDiagnostics} disabled={downloadingAuthBundle}>
                      {downloadingAuthBundle ? t('actions.downloading') : t('actions.downloadAuthDiagnostics')}
                    </Button>
                  )}
                  {canManageAuthDiagnostics && scanAuth.screenshotAvailable && (
                    <Button type="button" variant="secondary" onClick={handleOpenAuthScreenshot} disabled={openingAuthScreenshot}>
                      {openingAuthScreenshot ? t('actions.openingScreenshot') : t('actions.viewAuthScreenshot')}
                    </Button>
                  )}
                  <Link
                    to={summary ? `/sites/${summary.site.id}` : '/sites'}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: `1px solid ${C.border}`,
                      background: C.surface,
                      color: C.text,
                      textDecoration: 'none',
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {t('actions.openSiteAuthSettings')}
                  </Link>
                </div>
              )}
            </div>

            <AlertBanner
              variant={scanAuth.mode === 'public' ? 'neutral' : scanAuth.blockedByAuthFailure ? 'danger' : 'info'}
              icon={scanAuth.mode === 'public' ? 'ℹ' : scanAuth.blockedByAuthFailure ? '✕' : '✓'}
              style={{ marginBottom: 12 }}
            >
              {(scanAuth.mode === 'public'
                ? t('labels.authPublicMessage')
                : t('labels.authModeMessage', { mode: formatAuthMode(scanAuth.mode) }))}
            </AlertBanner>

            {scanAuth.blockedByAuthFailure && (
              <div style={{ fontSize: 13, color: C.danger, marginBottom: 8 }}>
                {t('labels.authBlockedMessage')}
              </div>
            )}

            {scanAuth.failureCategory && (
              <div style={{ fontSize: 13, color: C.text, marginBottom: 6 }}>
                {t('labels.failureCategory')}: <strong>{formatAuthFailureCategory(scanAuth.failureCategory)}</strong>
              </div>
            )}

            {scanAuth.consoleHint && (
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>
                {t('labels.consoleHint', { hint: scanAuth.consoleHint })}
              </div>
            )}

            {scanAuth.networkHint && (
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>
                {t('labels.networkHint', { hint: scanAuth.networkHint })}
              </div>
            )}

            {scanAuth.refreshAttempted && (
              <div style={{ fontSize: 12, color: C.muted }}>
                {scanAuth.refreshSucceeded
                  ? t('labels.authRefreshRecoveredBody')
                  : t('labels.authRefreshFailedBody')}
              </div>
            )}

            {scanAuth.mode !== 'public' && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 12,
                  marginTop: 14,
                }}
              >
                <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px', background: C.surfaceMuted }}>
                  <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                    {t('labels.authenticatedPagesScanned')}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: C.text, lineHeight: 1 }}>
                    {authenticatedPageCoverage}
                  </div>
                </div>
                <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px', background: C.surfaceMuted }}>
                  <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                    {t('labels.authenticatedRoutesDiscovered')}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: C.text, lineHeight: 1 }}>
                    {authenticatedRouteCoverage}
                  </div>
                </div>
              </div>
            )}

            {scanAuth.mode !== 'public' && (
              <div style={{ fontSize: 12, color: C.muted, marginTop: 12 }}>
                {authenticatedPageCoverage > 1 || authenticatedRouteCoverage > 1
                  ? t('labels.authCoverageExpanded', { pages: authenticatedPageCoverage, routes: authenticatedRouteCoverage })
                  : t('labels.authCoverageLimited')}
              </div>
            )}
          </Card>
        )}

        {canViewInternalReview && scanReview && (
          <div style={{ marginBottom: 24 }}>
            <ScanReviewPanel
              review={scanReview}
              showValidationHelper
              dataTestId="scan-review-panel"
            />
          </div>
        )}

        {/* ── Score hero + severity breakdown ────────────────────── */}
        {summary && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '200px 1fr',
              gap: 16,
              marginBottom: 24,
              alignItems: 'stretch',
            }}
          >
            {/* Score ring */}
            <Card
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '28px 20px',
                gap: 0,
              }}
            >
              {scanScore !== null ? (
                <>
                  <ScoreCard score={scanScore} size="lg" label={t('labels.score')} />
                  <div style={{ marginTop: 12 }}>
                    <Badge variant={getScoreVariant(scanScore)}>
                      {t(`common:${getScoreLabelKey(scanScore)}`)}
                    </Badge>
                  </div>
                </>
              ) : (
                <div style={{ color: C.muted, fontSize: 13 }}>{t('labels.scoreUnavailable')}</div>
              )}
            </Card>

            {/* Right column: severity strip + page/issue stats */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Severity strip */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 10,
                }}
              >
                {severityStrip.map(({ key, label, count, color, bg }) => (
                  <div
                    key={key}
                    style={{
                      background: count > 0 ? bg : C.surfaceMuted,
                      border: `1px solid ${count > 0 ? color + '40' : C.border}`,
                      borderRadius: 12,
                      padding: '14px 16px',
                      textAlign: 'center',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 26,
                        fontWeight: 800,
                        color: count > 0 ? color : C.muted,
                        lineHeight: 1,
                      }}
                    >
                      {count}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: count > 0 ? color : C.muted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginTop: 6,
                      }}
                    >
                      {label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pages + total issues row */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 10,
                }}
              >
                <StatCard
                  value={summary.scan.pagesDiscovered ?? 0}
                  label={t('labels.pagesFound')}
                />
                <StatCard
                  value={summary.scan.pagesScanned ?? 0}
                  label={t('labels.pagesScannedShort')}
                />
                <StatCard
                  value={summary.scan.pagesFailed ?? 0}
                  label={t('labels.pagesFailed')}
                  accent={(summary.scan.pagesFailed ?? 0) > 0 ? C.danger : undefined}
                />
                <StatCard
                  value={summary.scan.totalIssues ?? 0}
                  label={t('labels.totalIssues')}
                  accent={(summary.scan.totalIssues ?? 0) > 0 ? C.warning : undefined}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Priority findings ───────────────────────────────────── */}
        {priorityFindings.length > 0 && (
          <Card
            className="acc-priority-findings-alert"
            style={{
              marginBottom: 24,
              background: 'var(--acc-priority-findings-bg)',
              border: '1px solid var(--acc-priority-findings-border)',
            }}
          >
            <div
              className="acc-priority-findings-alert__heading"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--acc-priority-findings-text)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginBottom: 12,
              }}
            >
              <span aria-hidden="true" className="acc-priority-findings-alert__icon">!</span>
              <span>{t('sections.prioritySummary', { count: priorityFindings.length })}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {priorityFindings.map((entry) => (
                <div
                  key={entry.issueId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 14px',
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                  }}
                >
                  <Badge variant={impactVariant(entry.impact)}>{formatImpactLabel(entry.impact)}</Badge>
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.text }}>
                    {entry.title}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      color: C.danger,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {entry.occurrenceCount}×
                  </span>
                  <Badge variant="info">WCAG {entry.wcagCriteria}</Badge>
                </div>
              ))}
            </div>
            {issueSummary.filter((e) => e.impact === 'critical' || e.impact === 'serious').length > 5 && (
              <div style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>
                {t('labels.morePriorityIssues', { count: issueSummary.filter((e) => e.impact === 'critical' || e.impact === 'serious').length - 5 })}
              </div>
            )}
          </Card>
        )}

        {/* ── Tab bar ────────────────────────────────────────────── */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            padding: 10,
            border: `1px solid ${C.border}`,
            borderRadius: 18,
            background: 'var(--card-bg)',
            marginBottom: 24,
            boxShadow: 'var(--acc-shadow-xs)',
          }}
        >
          {(['issues', 'pages', 'evidence'] as Tab[]).map((tab) => (
            <button
              key={tab}
              style={{
                minHeight: 40,
                padding: '0 16px',
                border: `1px solid ${activeTab === tab ? 'rgba(38, 199, 190, 0.28)' : C.border}`,
                borderRadius: 999,
                background: activeTab === tab ? 'var(--surface-secondary)' : 'transparent',
                cursor: 'pointer',
                fontWeight: activeTab === tab ? 700 : 600,
                color: activeTab === tab ? 'var(--acc-color-info-text)' : C.muted,
                fontSize: 14,
                fontFamily: 'inherit',
                boxShadow: activeTab === tab ? 'inset 0 0 0 1px rgba(38, 199, 190, 0.08)' : 'none',
              }}
              onClick={() => setActiveTab(tab)}
            >
              {t(`sections.${tab}`)}
              {tab === 'issues' && issueSummary.length > 0 && (
                <span style={{ fontSize: 12, marginLeft: 4, color: C.muted, fontWeight: 400 }}>
                  {t('labels.typesCount', { count: issueSummary.length })}
                </span>
              )}
              {tab === 'pages' && pages.length > 0 && (
                <span style={{ fontSize: 12, marginLeft: 4, color: C.muted, fontWeight: 400 }}>
                  ({pages.length})
                </span>
              )}
              {tab === 'evidence' && evidence.length > 0 && (
                <span style={{ fontSize: 12, marginLeft: 4, color: C.muted, fontWeight: 400 }}>
                  ({evidence.length})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Issues tab ─────────────────────────────────────────── */}
        {activeTab === 'issues' && (
          sortedIssueSummary.length === 0 ? (
            <EmptyState
              icon="✅"
              title={t('empty.issuesTitle')}
              description={t('empty.issuesDescription')}
            />
          ) : (
            <>
              <SectionHeader
                eyebrow={t('sections.issues')}
                title={t('sections.issueTriageTitle')}
                action={(
                  <div
                    style={{
                      display: 'grid',
                      gap: 6,
                      justifyItems: 'end',
                      maxWidth: 280,
                    }}
                  >
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleExportIssuesCsv}
                      disabled={filteredIssueSummary.length === 0}
                    >
                      {t('actions.exportIssues')}
                    </Button>
                    <div
                      style={{
                        fontSize: 12,
                        lineHeight: 1.5,
                        color: 'var(--acc-color-text-muted)',
                        textAlign: 'right',
                      }}
                    >
                      {t('labels.exportIssuesHelper')}
                    </div>
                  </div>
                )}
                style={{ marginBottom: 16 }}
              />

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '320px minmax(0, 1fr)',
                  gap: 18,
                  alignItems: 'start',
                }}
              >
                <Card
                  style={{
                    position: 'sticky',
                    top: 'calc(var(--acc-nav-height) + 16px)',
                    padding: 20,
                  }}
                >
                  <div style={{ display: 'grid', gap: 16 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                      {[
                        [formatIssueDeliveryStatus('open'), issueStatusSummary.open],
                        [formatIssueDeliveryStatus('in_progress'), issueStatusSummary.inProgress],
                        [formatIssueDeliveryStatus('fixed'), issueStatusSummary.fixed],
                        [t('labels.suppressedShort'), issueStatusSummary.suppressed],
                      ].map(([label, count]) => (
                        <div
                          key={label}
                          style={{
                            border: '1px solid var(--acc-color-border)',
                            borderRadius: 14,
                            padding: '12px 12px 10px',
                            background: 'var(--acc-color-surface-muted)',
                          }}
                        >
                          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 5 }}>
                            {label}
                          </div>
                          <div style={{ fontSize: 22, fontWeight: 800, color: C.text }}>{count}</div>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: 'grid', gap: 8 }}>
                      <div style={{ fontSize: 11, color: C.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                        {t('labels.severityFilter')}
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {(['all', 'critical', 'serious', 'moderate', 'minor'] as const).map((impact) => (
                          <button
                            key={impact}
                            type="button"
                            onClick={() => setSeverityFilter(impact)}
                            style={{
                              minHeight: 34,
                              padding: '0 12px',
                              borderRadius: 999,
                              border: severityFilter === impact ? '1px solid rgba(14, 165, 164, 0.22)' : '1px solid var(--acc-color-border)',
                              background: severityFilter === impact ? C.accentSoft : C.surface,
                              color: severityFilter === impact ? C.text : C.text,
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            {impact === 'all' ? t('labels.all') : formatImpactLabel(impact)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gap: 8 }}>
                      <div style={{ fontSize: 11, color: C.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                        {t('labels.workflowFilter')}
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {(['all', 'open', 'in_progress', 'fixed'] as const).map((state) => (
                          <button
                            key={state}
                            type="button"
                            onClick={() => setWorkflowFilter(state)}
                            style={{
                              minHeight: 34,
                              padding: '0 12px',
                              borderRadius: 999,
                              border: workflowFilter === state ? '1px solid rgba(14, 165, 164, 0.22)' : '1px solid var(--acc-color-border)',
                              background: workflowFilter === state ? C.accentSoft : C.surface,
                              color: workflowFilter === state ? C.text : C.text,
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                              {state === 'all' ? t('labels.all') : formatIssueDeliveryStatus(state)}
                          </button>
                        ))}
                      </div>
                    </div>

                    {filteredIssueSummary.length === 0 ? (
                      <AlertBanner variant="warning">
                        {t('labels.noIssuesForFilters')}
                      </AlertBanner>
                    ) : (
                      <div style={{ display: 'grid', gap: 8 }}>
                        {filteredIssueSummary.map((entry) => (
                          <button
                            key={entry.issueId}
                            type="button"
                            onClick={() => {
                              setSelectedIssueId(entry.issueId);
                              document.getElementById(`issue-${entry.issueId}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
                            }}
                            style={{
                              width: '100%',
                              textAlign: 'left',
                              display: 'grid',
                              gap: 8,
                              padding: '14px',
                              borderRadius: 16,
                              border: selectedIssueEntry?.issueId === entry.issueId ? '1px solid rgba(14, 165, 164, 0.26)' : '1px solid var(--acc-color-border)',
                               background: selectedIssueEntry?.issueId === entry.issueId ? `linear-gradient(180deg, ${C.surface} 0%, ${C.accentSoft} 100%)` : C.surface,
                              boxShadow: selectedIssueEntry?.issueId === entry.issueId ? '0 12px 24px rgba(14, 165, 164, 0.08)' : 'none',
                              cursor: 'pointer',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 11, color: C.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>
                                  {entry.code}
                                </div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: C.text, lineHeight: 1.45 }}>
                                  {entry.title}
                                </div>
                              </div>
                              <Badge variant={impactVariant(entry.impact)}>{formatImpactLabel(entry.impact)}</Badge>
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <Badge variant={issueDeliveryStatusVariant(resolveIssueDeliveryStatus(entry.workflowState))}>
                                {formatIssueDeliveryStatus(resolveIssueDeliveryStatus(entry.workflowState))}
                              </Badge>
                              {entry.suppressedCount > 0 && <Badge variant="neutral">{t('labels.suppressedCompact', { count: entry.suppressedCount })}</Badge>}
                            </div>
                            <div style={{ fontSize: 12, color: C.muted }}>
                              {t('labels.occurrenceAcrossPages', {
                                occurrences: entry.occurrenceCount,
                                pages: entry.affectedPages,
                              })}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>

                <div style={{ display: 'grid', gap: 16 }}>
              {filteredIssueSummary.map((entry) => {
                const instances = issueInstances.filter((i) => i.issue?.code === entry.code);
                const sampleInstance = instances[0] ?? null;
                const sampleEvidence = sampleInstance?.pageEvidence ?? null;
                const sampleRoute = sampleInstance?.pageUrl ?? entry.samplePageUrl ?? null;
                const reviewedSeverity = resolveReviewedSeverity(entry.impact, entry.severityOverride);
                const hasReview = hasAutomatedFindingReview(entry);
                return (
                  <div
                    key={entry.issueId}
                    id={`issue-${entry.issueId}`}
                    style={{ scrollMarginTop: 'calc(var(--acc-nav-height) + 24px)' }}
                  >
                  <Card
                    style={{
                      border: selectedIssueEntry?.issueId === entry.issueId ? '1px solid rgba(14, 165, 164, 0.24)' : undefined,
                      boxShadow: selectedIssueEntry?.issueId === entry.issueId ? '0 18px 36px rgba(14, 165, 164, 0.08)' : undefined,
                    }}
                  >
                    {/* Issue header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 14 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
                          {entry.code}
                        </div>
                        <div style={{ fontWeight: 700, fontSize: 19, color: C.text, lineHeight: 1.3, marginBottom: 10 }}>
                          {entry.title}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const }}>
                          <Badge variant={impactVariant(entry.impact)}>{formatImpactLabel(entry.impact)}</Badge>
                          {entry.severityOverride && (
                            <Badge variant={impactVariant(entry.severityOverride)}>
                              {t('labels.reviewedSeverity', { value: formatImpactLabel(entry.severityOverride) })}
                            </Badge>
                          )}
                          <Badge variant="info">WCAG {entry.wcagCriteria}</Badge>
                          <Badge variant="info">{entry.wcagLevel}</Badge>
                          <Badge variant={issueDeliveryStatusVariant(resolveIssueDeliveryStatus(entry.workflowState))}>
                            {formatIssueDeliveryStatus(resolveIssueDeliveryStatus(entry.workflowState))}
                          </Badge>
                          {entry.suppressedCount > 0 && (
                            <Badge variant="neutral">{t('labels.suppressedCompact', { count: entry.suppressedCount })}</Badge>
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          minWidth: 176,
                          border: `1px solid ${C.border}`,
                          borderRadius: 12,
                          background: C.surfaceMuted,
                          padding: '14px 16px',
                        }}
                      >
                        <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                          {t('labels.scopeTitle')}
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 800, color: C.text, lineHeight: 1 }}>
                          {entry.occurrenceCount}
                        </div>
                        <div style={{ fontSize: 12, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
                          {t('labels.occurrenceAcrossPages', {
                            occurrences: entry.occurrenceCount,
                            pages: entry.affectedPages,
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Explanation */}
                    <div
                      style={{
                        fontSize: 13,
                        color: C.text,
                        lineHeight: 1.7,
                        borderLeft: `3px solid ${C.border}`,
                        paddingLeft: 12,
                      }}
                    >
                      {entry.plainEnglishExplanation}
                    </div>

                    <div
                      style={{
                        marginTop: 12,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: 12,
                      }}
                    >
                      <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, background: C.surfaceMuted, padding: '14px 16px' }}>
                        <div
                          style={{
                            fontSize: 11,
                            color: C.muted,
                            fontWeight: 700,
                            textTransform: 'uppercase' as const,
                            letterSpacing: '0.05em',
                            marginBottom: 6,
                          }}
                        >
                          {t('labels.likelyCause')}
                        </div>
                        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.65 }}>
                          {entry.likelyCause?.trim() || t('common:review.honesty.noLikelyCause')}
                        </div>
                      </div>

                      <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, background: C.surfaceMuted, padding: '14px 16px' }}>
                        <div
                          style={{
                            fontSize: 11,
                            color: C.muted,
                            fontWeight: 700,
                            textTransform: 'uppercase' as const,
                            letterSpacing: '0.05em',
                            marginBottom: 6,
                          }}
                        >
                          {t('labels.developerGuidance')}
                        </div>
                        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.65 }}>
                          {entry.developerFixGuidance?.trim() || t('common:review.honesty.noDeveloperGuidance')}
                        </div>
                      </div>

                      <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, background: C.surfaceMuted, padding: '14px 16px' }}>
                        <div
                          style={{
                            fontSize: 11,
                            color: C.muted,
                            fontWeight: 700,
                            textTransform: 'uppercase' as const,
                            letterSpacing: '0.05em',
                            marginBottom: 6,
                          }}
                        >
                          {t('labels.suggestedRemediation')}
                        </div>
                        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.65 }}>
                          {entry.remediationGuidance?.trim() || t('common:review.honesty.noRemediationGuidance')}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        marginTop: 12,
                        padding: '14px 16px',
                        borderRadius: 12,
                        border: `1px solid ${C.border}`,
                        background: C.surfaceMuted,
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 12,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <div style={{ minWidth: 0, flex: 1, fontSize: 12, color: C.text }}>
                        <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                          {t('labels.evidenceRouteContext')}
                        </div>
                        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.65 }}>
                          {sampleRoute ? (
                            <>
                              {t('labels.sampleAffectedRoute')}
                              <div style={{ wordBreak: 'break-all' as const, marginTop: 4 }}>
                                {sampleRoute}
                              </div>
                            </>
                          ) : t('common:review.honesty.noSampleRoute')}
                        </div>
                        {!sampleEvidence && (
                          <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>
                            {t('common:review.honesty.noEvidence')}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {sampleEvidence && (
                          <Button type="button" variant="secondary" onClick={() => handleOpenEvidence(sampleEvidence.id)}>
                            {t('actions.openPageEvidence')}
                          </Button>
                        )}
                        {summary && (
                          <Link
                            to={`/sites/${summary.site.id}/compliance`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: 40,
                              padding: '9px 16px',
                              borderRadius: 10,
                              border: `1px solid ${C.border}`,
                              background: C.surface,
                              color: C.text,
                              textDecoration: 'none',
                              fontSize: 14,
                              fontWeight: 600,
                            }}
                          >
                            {t('actions.openCompliance')}
                          </Link>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        marginTop: 12,
                        padding: '14px 16px',
                        borderRadius: 12,
                        border: `1px solid ${C.border}`,
                        background: C.surfaceMuted,
                        display: 'grid',
                        gap: 12,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                            {t('labels.review')}
                          </div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Badge variant={issueDeliveryStatusVariant(resolveIssueDeliveryStatus(entry.workflowState))}>
                              {formatIssueDeliveryStatus(resolveIssueDeliveryStatus(entry.workflowState))}
                            </Badge>
                            {entry.severityOverride && (
                              <Badge variant={impactVariant(reviewedSeverity)}>
                                {t('labels.reviewedSeverity', { value: formatImpactLabel(reviewedSeverity) })}
                              </Badge>
                            )}
                            {entry.suppressedCount > 0 && (
                              <Badge variant="neutral">
                                {t('labels.suppressedCompact', { count: entry.suppressedCount })}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'grid', gap: 8, justifyItems: 'end' }}>
                          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                            {entry.workflowUpdatedAt
                              ? t('labels.updatedAt', { value: formatDateTime(entry.workflowUpdatedAt) })
                              : t('labels.notReviewedYet')}
                          </div>
                          {canReviewFindings && (
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              onClick={() => openIssueReviewModal(entry)}
                            >
                              {hasReview ? t('actions.editReview') : t('actions.reviewIssue')}
                            </Button>
                          )}
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: C.muted }}>
                        <span>{t('labels.occurrences', { count: entry.occurrenceCount })}</span>
                        <span>{t('labels.affectedPages', { count: entry.affectedPages })}</span>
                        <span>{t('labels.suppressedCount', { count: entry.suppressedCount })}</span>
                        {entry.severityOverride && (
                          <span>{t('labels.severityOverrideSummary', { value: formatImpactLabel(entry.severityOverride) })}</span>
                        )}
                      </div>

                      {entry.workflowNote && (
                        <div style={{ fontSize: 12, color: C.text, lineHeight: 1.6 }}>
                          <strong>{t('labels.analystNotes')}</strong> {entry.workflowNote}
                        </div>
                      )}

                      {reviewSuccess?.issueId === entry.issueId && (
                        <div
                          role="status"
                          style={{
                            outline: 'none',
                            background: C.successSoft,
                            border: `1px solid ${C.success}`,
                            borderRadius: 10,
                            padding: '10px 12px',
                            color: C.success,
                            fontSize: 12,
                            fontWeight: 600,
                            lineHeight: 1.5,
                          }}
                        >
                          {reviewSuccess.message}
                        </div>
                      )}

                      <div
                        style={{
                          display: 'grid',
                          gap: 10,
                          border: `1px solid ${C.border}`,
                          borderRadius: 14,
                          background: entry.externalLink ? C.surfaceMuted : C.surface,
                          padding: '12px 14px',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            gap: 12,
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                          }}
                        >
                          <div style={{ display: 'grid', gap: 4, minWidth: 0, flex: '1 1 280px' }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>
                                {t('labels.githubIssue')}
                              </span>
                              <Badge variant="neutral">GitHub</Badge>
                              {entry.externalLink && (
                                <Badge variant={externalIssueStatusVariant(entry.externalLink.externalStatus)}>
                                  {formatExternalIssueStatus(entry.externalLink.externalStatus)}
                                </Badge>
                              )}
                            </div>
                            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                              {t('labels.githubIssueHelper')}
                            </div>
                            {entry.externalLink && (
                              <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
                                {t('labels.githubIssueSyncHelper')}
                              </div>
                            )}
                            {entry.externalLink ? (
                              <>
                                <a
                                  href={entry.externalLink.externalIssueUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: C.link,
                                    textDecoration: 'none',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    wordBreak: 'break-all',
                                  }}
                                >
                                  {buildGitHubIssueLabel(entry.externalLink)}
                                </a>
                                <div style={{ fontSize: 12, color: C.muted }}>
                                  {entry.externalLink.lastSyncedAt
                                    ? t('labels.lastSyncedAt', { value: formatDateTime(entry.externalLink.lastSyncedAt) })
                                    : t('labels.lastSyncedNever')}
                                </div>
                              </>
                            ) : externalIntegrations.github.defaultRepository ? (
                              <div style={{ fontSize: 12, color: C.text }}>
                                {t('labels.defaultGitHubRepository', { value: externalIntegrations.github.defaultRepository })}
                              </div>
                            ) : (
                              <div style={{ fontSize: 12, color: C.muted }}>
                                {t('labels.githubIssueCreateUnavailable')}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            {!entry.externalLink && externalIntegrations.github.canCreate && (
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => void handleCreateGitHubIssue(entry)}
                                disabled={externalIssueBusy?.issueId === entry.issueId}
                              >
                                {externalIssueBusy?.issueId === entry.issueId && externalIssueBusy.action === 'create'
                                  ? t('actions.creatingGitHubIssue')
                                  : t('actions.createGitHubIssue')}
                              </Button>
                            )}
                            {entry.externalLink && (
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => void handleSyncGitHubIssue(entry)}
                                disabled={externalIssueBusy?.issueId === entry.issueId}
                              >
                                {externalIssueBusy?.issueId === entry.issueId && externalIssueBusy.action === 'sync'
                                  ? t('actions.syncingGitHubStatus')
                                  : t('actions.syncGitHubStatus')}
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                setExternalIssueFeedback(null);
                                setExternalLinkEditorIssueId((current) => current === entry.issueId ? null : entry.issueId);
                              }}
                            >
                              {entry.externalLink ? t('actions.linkDifferentGitHubIssue') : t('actions.linkGitHubIssue')}
                            </Button>
                          </div>
                        </div>

                        {externalLinkEditorIssueId === entry.issueId && (
                          <div
                            style={{
                              display: 'grid',
                              gap: 10,
                              borderTop: `1px solid ${C.border}`,
                              paddingTop: 10,
                            }}
                          >
                            <label style={{ display: 'grid', gap: 4, fontSize: 12, color: C.muted }}>
                              {t('labels.externalIssueUrl')}
                              <input
                                type="url"
                                value={externalIssueDrafts[entry.issueId] ?? ''}
                                onChange={(event) => updateExternalIssueDraft(entry.issueId, event.target.value)}
                                placeholder="https://github.com/owner/repo/issues/123"
                                style={{
                                  border: `1px solid ${C.border}`,
                                  borderRadius: 8,
                                  padding: '9px 10px',
                                  fontSize: 13,
                                  color: C.text,
                                  background: C.surface,
                                }}
                              />
                              <span style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
                                {t('labels.externalIssueUrlHint')}
                              </span>
                            </label>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => void handleLinkGitHubIssue(entry)}
                                disabled={
                                  externalIssueBusy?.issueId === entry.issueId
                                  || (externalIssueDrafts[entry.issueId] ?? '').trim().length === 0
                                }
                              >
                                {externalIssueBusy?.issueId === entry.issueId && externalIssueBusy.action === 'link'
                                  ? t('actions.linkingGitHubIssue')
                                  : entry.externalLink
                                    ? t('actions.linkDifferentGitHubIssue')
                                    : t('actions.linkGitHubIssue')}
                              </Button>
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => setExternalLinkEditorIssueId(null)}
                                disabled={externalIssueBusy?.issueId === entry.issueId}
                              >
                                {t('actions.cancel')}
                              </Button>
                            </div>
                          </div>
                        )}

                        {externalIssueFeedback?.issueId === entry.issueId && (
                          <AlertBanner
                            variant={externalIssueFeedback.variant === 'danger' ? 'danger' : 'success'}
                            style={{ fontSize: 12, padding: '10px 12px' }}
                          >
                            <div>
                              <div>{externalIssueFeedback.message}</div>
                              {externalIssueFeedback.detail && (
                                <div style={{ marginTop: 4, opacity: 0.9 }}>
                                  {externalIssueFeedback.detail}
                                </div>
                              )}
                            </div>
                          </AlertBanner>
                        )}
                      </div>

                      {summary && (
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => void handleCopyIssueForJira(entry)}
                            disabled={jiraCopyState?.issueId === entry.issueId && jiraCopyState.state === 'copying'}
                          >
                            {jiraCopyState?.issueId === entry.issueId
                              ? jiraCopyState.state === 'copying'
                                ? t('actions.copyingForJira')
                                : jiraCopyState.state === 'copied'
                                  ? t('actions.copiedForJira')
                                  : t('actions.copyForJiraFailed')
                              : t('actions.copyForJira')}
                          </Button>
                          <Link to={`/scans/${summary.scan.id}`} style={{ color: C.link, textDecoration: 'none', fontSize: 12, fontWeight: 600 }}>
                            {t('labels.keepScanContext')}
                          </Link>
                          <Link to={`/sites/${summary.site.id}/compliance`} style={{ color: C.link, textDecoration: 'none', fontSize: 12, fontWeight: 600 }}>
                            {t('labels.moveToCompliance')}
                          </Link>
                        </div>
                      )}

                    </div>

                    {/* Occurrences */}
                    {instances.length > 0 && (
                      <details
                        style={{ marginTop: 2 }}
                        open={openOccurrenceIssueIds.includes(entry.issueId)}
                        onToggle={(event) => {
                          setOccurrenceDetailOpen(entry.issueId, event.currentTarget.open);
                        }}
                      >
                        <summary
                          style={{
                            cursor: 'pointer',
                            fontSize: 13,
                            color: C.link,
                            fontWeight: 600,
                          }}
                        >
                          {t('sections.occurrenceDetail', { shown: Math.min(instances.length, 20), total: instances.length })}
                        </summary>
                        {suppressionSuccess?.issueId === entry.issueId && (
                          <div
                            ref={suppressionSuccessRef}
                            tabIndex={-1}
                            role="status"
                            style={{
                              outline: 'none',
                              marginBottom: 12,
                              background: C.successSoft,
                              border: `1px solid ${C.success}`,
                              borderRadius: 12,
                              padding: '14px 16px',
                              color: C.success,
                              fontSize: 13,
                              fontWeight: 600,
                              lineHeight: 1.55,
                              boxShadow: '0 8px 20px rgba(5, 150, 105, 0.12)',
                            }}
                          >
                            {suppressionSuccess.message}
                          </div>
                        )}
                        <div style={{ overflowX: 'visible' }}>
                        <table style={{ ...tbl.table, marginTop: 4 }}>
                          <caption className="acc-sr-only">
                            {t('sections.occurrenceDetail', { shown: Math.min(instances.length, 20), total: instances.length })}
                          </caption>
                          <colgroup>
                            <col style={{ width: '22%' }} />
                            <col style={{ width: '12%' }} />
                            <col style={{ width: '22%' }} />
                            <col style={{ width: '11%' }} />
                            <col style={{ width: '13%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                          </colgroup>
                          <thead>
                            <tr>
                              <th scope="col" style={tbl.th}>{t('labels.tablePage')}</th>
                              <th scope="col" style={tbl.th}>{t('labels.tableSelector')}</th>
                              <th scope="col" style={tbl.th}>{t('labels.tableHtmlSnippet')}</th>
                              <th scope="col" style={tbl.th}>{t('labels.tableWorkflow')}</th>
                              <th scope="col" style={tbl.th}>{t('labels.tableSuppression')}</th>
                              <th scope="col" style={tbl.th}>{t('labels.tableEvidence')}</th>
                              <th scope="col" style={tbl.th}>{t('labels.tableAction')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {instances.slice(0, 20).map((inst) => (
                              <Fragment key={inst.id}>
                                <tr>
                                  <th scope="row" style={{ ...tbl.td, minWidth: 0 }}>
                                    {inst.pageUrl ? (
                                      <div style={{ display: 'grid', gap: 4 }}>
                                        <a
                                          href={inst.pageUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ color: C.link, textDecoration: 'none', fontSize: 12, wordBreak: 'break-all' as const }}
                                        >
                                          {inst.pageUrl}
                                        </a>
                                        {inst.pageTitle && (
                                          <span style={{ fontSize: 11, color: C.muted }}>
                                            {inst.pageTitle}
                                          </span>
                                        )}
                                      </div>
                                    ) : t('common:review.honesty.noRouteRecorded')}
                                  </th>
                                  <td style={{ ...tbl.td, minWidth: 0 }}>
                                    {inst.selector ? (
                                      <code style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' as const }}>{inst.selector}</code>
                                    ) : (
                                      <span style={{ fontSize: 12, color: C.muted }}>{t('common:review.honesty.noSelector')}</span>
                                    )}
                                  </td>
                                  <td style={{ ...tbl.td, minWidth: 0 }}>
                                    {inst.htmlSnippet ? (
                                      <div
                                        style={{
                                          fontFamily: 'monospace',
                                          fontSize: 11,
                                          background: C.surfaceMuted,
                                          border: `1px solid ${C.border}`,
                                          borderRadius: 4,
                                          padding: '4px 8px',
                                          maxWidth: '100%',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap' as const,
                                        }}
                                        title={inst.htmlSnippet}
                                      >
                                        {inst.htmlSnippet}
                                      </div>
                                    ) : (
                                      <span style={{ fontSize: 12, color: C.muted }}>{t('common:review.honesty.noDomSnippet')}</span>
                                    )}
                                  </td>
                                  <td style={tbl.td}>
                                    <div style={{ display: 'grid', gap: 4 }}>
                                      <Badge variant={issueDeliveryStatusVariant(resolveIssueDeliveryStatus(inst.workflowState))}>
                                        {formatIssueDeliveryStatus(resolveIssueDeliveryStatus(inst.workflowState))}
                                      </Badge>
                                      {inst.workflowAssigneeName && (
                                        <span style={{ fontSize: 11, color: C.muted }}>
                                          {inst.workflowAssigneeName}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td style={tbl.td}>
                                    {inst.suppression ? (
                                      <div
                                        style={{
                                          display: 'grid',
                                          gap: 6,
                                          padding: suppressionSuccess?.instanceId === inst.id ? '10px 12px' : 0,
                                          borderRadius: 10,
                                          background: suppressionSuccess?.instanceId === inst.id ? C.successSoft : 'transparent',
                                          border: suppressionSuccess?.instanceId === inst.id ? `1px solid ${C.success}` : 'none',
                                        }}
                                      >
                                        <Badge variant="neutral">
                                          {inst.suppression.matchedBy === 'selector' ? t('labels.selectorSuppressed') : t('labels.pageSuppressed')}
                                        </Badge>
                                        <span style={{ fontSize: 11, color: C.muted }}>
                                          {t('labels.reviewBy', { value: inst.suppression.expiresAt ? formatDate(inst.suppression.expiresAt) : '—' })}
                                        </span>
                                        {suppressionSuccess?.instanceId === inst.id && (
                                          <span style={{ fontSize: 11, fontWeight: 600, color: C.success }}>
                                            {t('labels.updatedJustNow')}
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      <span style={{ color: C.muted }}>{t('labels.active')}</span>
                                    )}
                                  </td>
                                  <td style={tbl.td}>
                                    {inst.pageEvidence ? (
                                      <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={() => handleOpenEvidence(inst.pageEvidence!.id)}
                                        style={{ width: '100%', minWidth: 0, whiteSpace: 'normal' }}
                                      >
                                        {t('actions.viewEvidence')}
                                      </Button>
                                    ) : (
                                      <span style={{ fontSize: 12, color: C.muted }}>{t('common:review.honesty.noEvidence')}</span>
                                    )}
                                  </td>
                                  <td style={{ ...tbl.td, minWidth: 0, verticalAlign: 'top' }}>
                                    {canManageSuppressions ? (
                                      <div style={{ display: 'grid', gap: 8, width: '100%' }}>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxWidth: '100%' }}>
                                          {inst.suppression ? (
                                            <Button
                                              type="button"
                                              variant="secondary"
                                              onClick={() => void handleClearSuppression(inst, entry.issueId)}
                                              disabled={suppressionBusyInstanceId === inst.id}
                                              style={{ width: '100%', minWidth: 0, whiteSpace: 'normal' }}
                                            >
                                              {suppressionBusyInstanceId === inst.id ? t('actions.clearingSuppression') : t('actions.clearSuppression')}
                                            </Button>
                                          ) : (
                                            <Button
                                              type="button"
                                              variant="secondary"
                                              onClick={() => openSuppressionEditor(inst, entry.issueId)}
                                              style={{ width: '100%', minWidth: 0, whiteSpace: 'normal' }}
                                            >
                                              {t('actions.suppressOccurrence')}
                                            </Button>
                                          )}
                                        </div>
                                        {suppressionError?.instanceId === inst.id && suppressionEditorInstanceId !== inst.id && (
                                          <AlertBanner variant="danger" style={{ fontSize: 12, padding: '10px 12px' }}>
                                            <div>
                                              <div>{suppressionError.message}</div>
                                              {suppressionError.detail && (
                                                <div style={{ marginTop: 4, fontSize: 12, opacity: 0.9 }}>
                                                  {suppressionError.detail}
                                                </div>
                                              )}
                                            </div>
                                          </AlertBanner>
                                        )}
                                      </div>
                                    ) : (
                                      <span style={{ color: C.muted }}>—</span>
                                    )}
                                  </td>
                                </tr>
                                {canManageSuppressions && suppressionEditorInstanceId === inst.id && !inst.suppression && (
                                  <tr>
                                    <td
                                      colSpan={7}
                                      style={{
                                        ...tbl.td,
                                        background: C.surfaceMuted,
                                        padding: '16px 18px 18px',
                                        borderTop: `1px solid ${C.border}`,
                                        borderBottom: `1px solid ${C.border}`,
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: 'grid',
                                          gap: 14,
                                          border: `1px solid ${C.border}`,
                                          borderRadius: 12,
                                          background: C.surface,
                                          padding: '18px',
                                          boxShadow: 'var(--acc-shadow-xs)',
                                        }}
                                      >
                                        <div style={{ display: 'grid', gap: 4 }}>
                                          <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>
                                            {t('labels.suppressTitle')}
                                          </div>
                                          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                                            {t('labels.suppressDescription')}
                                          </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, alignItems: 'start' }}>
                                          <label style={{ display: 'grid', gap: 4, fontSize: 12, color: C.muted }}>
                                            {t('labels.scope')}
                                            <select
                                              value={suppressionScope}
                                              onChange={(event) => setSuppressionScope(event.target.value as 'page' | 'selector')}
                                              style={{ width: '100%', minWidth: 0, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 10px', fontSize: 13, color: C.text, background: C.surface }}
                                            >
                                              {inst.selector && <option value="selector">{t('labels.allMatchingSelector')}</option>}
                                              <option value="page">{t('labels.allOnPage')}</option>
                                            </select>
                                            <span style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
                                              {t('labels.suppressionScopeHint')}
                                            </span>
                                          </label>
                                          <label style={{ display: 'grid', gap: 4, fontSize: 12, color: C.muted }}>
                                            {t('labels.reviewDate')}
                                            <input
                                              type="date"
                                              value={suppressionExpiry}
                                              onChange={(event) => setSuppressionExpiry(event.target.value)}
                                              style={{ width: '100%', minWidth: 0, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 10px', fontSize: 13, color: C.text, background: C.surface }}
                                            />
                                          </label>
                                        </div>
                                        <label style={{ display: 'grid', gap: 4, fontSize: 12, color: C.muted }}>
                                          {t('labels.suppressionNote')}
                                          <textarea
                                            value={suppressionReason}
                                            onChange={(event) => setSuppressionReason(event.target.value)}
                                            rows={3}
                                            style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 10px', fontSize: 13, color: C.text, background: C.surface, fontFamily: 'inherit', resize: 'vertical' as const }}
                                          />
                                        </label>
                                        {suppressionError?.instanceId === inst.id && (
                                          <AlertBanner
                                            variant="danger"
                                            role="alert"
                                            style={{ fontSize: 12, padding: '10px 12px' }}
                                          >
                                            <div ref={suppressionErrorRef} tabIndex={-1} style={{ outline: 'none' }}>
                                              <div>{suppressionError.message}</div>
                                              {suppressionError.detail && (
                                                <div style={{ marginTop: 4, fontSize: 12, opacity: 0.9 }}>
                                                  {suppressionError.detail}
                                                </div>
                                              )}
                                            </div>
                                          </AlertBanner>
                                        )}
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                                          <Button
                                            type="button"
                                            onClick={() => void handleSaveSuppression(inst, entry.issueId)}
                                            disabled={suppressionBusyInstanceId === inst.id || suppressionReason.trim().length === 0}
                                          >
                                            {suppressionBusyInstanceId === inst.id ? t('actions.savingSuppression') : t('actions.saveSuppression')}
                                          </Button>
                                          <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={resetSuppressionEditor}
                                            disabled={suppressionBusyInstanceId === inst.id}
                                          >
                                            {t('actions.cancel')}
                                          </Button>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            ))}
                            {instances.length > 20 && (
                              <tr>
                                <td colSpan={7} style={{ ...tbl.td, color: C.muted, fontStyle: 'italic' }}>
                                  {t('labels.moreOccurrences', { count: instances.length - 20 })}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        </div>
                      </details>
                    )}
                  </Card>
                  </div>
                );
              })}
                </div>
              </div>
            </>
          )
        )}

        {/* ── Pages tab ──────────────────────────────────────────── */}
        {activeTab === 'pages' && (
          pages.length === 0 ? (
            <EmptyState
              icon="📄"
              title={t('empty.pagesTitle')}
              description={t('empty.pagesDescription')}
            />
          ) : (
            <>
              <SectionHeader
                eyebrow={t('sections.pages')}
                title={t('sections.crawledPages')}
                style={{ marginBottom: 16 }}
              />
              <Card style={{ padding: 0, overflow: 'hidden' }}>
                <table style={tbl.table}>
                  <caption className="acc-sr-only">{t('sections.crawledPages')}</caption>
                  <thead>
                    <tr>
                      <th scope="col" style={tbl.th}>{t('labels.url')}</th>
                      <th scope="col" style={tbl.th}>{t('labels.title')}</th>
                      <th scope="col" style={tbl.th}>{t('labels.status')}</th>
                      <th scope="col" style={tbl.th}>{t('labels.http')}</th>
                      <th scope="col" style={tbl.th}>{t('sections.issues')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pages.map((pg) => (
                      <tr
                        key={pg.id}
                        style={pg.status === 'failed' ? { background: C.dangerSoft } : undefined}
                      >
                        <th scope="row" style={tbl.td}>
                          <div style={{ wordBreak: 'break-all' as const }}>
                            <a
                              href={pg.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: C.link,
                                fontSize: 12,
                                textDecoration: 'none',
                              }}
                            >
                              {pg.url}
                            </a>
                          </div>
                          {pg.status === 'failed' && pg.failureReason && (
                            <div
                              style={{
                                fontSize: 11,
                                color: C.danger,
                                marginTop: 3,
                                fontStyle: 'italic',
                              }}
                            >
                              {resolveScanFailureSummary(t, {
                                status: pg.status,
                                httpStatusCode: pg.httpStatusCode,
                                failureReason: pg.failureReason,
                              })}
                            </div>
                          )}
                        </th>
                        <td style={{ ...tbl.td, color: C.muted }}>{pg.title ?? '—'}</td>
                        <td style={tbl.td}>
                          <Badge variant={statusVariant(pg.status)}>{formatScanStatus(pg.status)}</Badge>
                        </td>
                        <td style={{ ...tbl.td, color: C.muted }}>{pg.httpStatusCode ?? '—'}</td>
                        <td style={tbl.td}>
                          {pg.status === 'failed' ? (
                            <span style={{ color: C.danger, fontSize: 12 }}>—</span>
                          ) : pg.issueCount > 0 ? (
                            <strong style={{ color: C.danger }}>{pg.issueCount}</strong>
                          ) : (
                            <span style={{ color: C.muted }}>{pg.issueCount}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </>
          )
        )}

        {/* ── Evidence tab ───────────────────────────────────────── */}
        {activeTab === 'evidence' && (
          <>
            {evidenceError && (
              <AlertBanner variant="danger" style={{ marginBottom: 16 }}>
                {evidenceError}
              </AlertBanner>
            )}
            {evidence.length === 0 ? (
              <EmptyState
                icon="📁"
                title={t('empty.evidenceTitle')}
                description={t('empty.evidenceDescription')}
              />
            ) : (
              <Card style={{ padding: 0, overflow: 'hidden' }}>
                <table style={tbl.table}>
                  <caption className="acc-sr-only">{t('sections.evidence')}</caption>
                  <thead>
                    <tr>
                      <th scope="col" style={tbl.th}>{t('labels.evidenceType')}</th>
                      <th scope="col" style={tbl.th}>{t('labels.fileSize')}</th>
                      <th scope="col" style={tbl.th}>{t('labels.hash')}</th>
                      <th scope="col" style={tbl.th}>{t('labels.created')}</th>
                      <th scope="col" style={tbl.th}>{t('actions.view')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {evidence.map((ev) => (
                      <tr key={ev.id}>
                        <th scope="row" style={tbl.td}>
                          <Badge variant="info">{formatEvidenceType(ev.recordType)}</Badge>
                        </th>
                        <td style={{ ...tbl.td, color: C.muted }}>{formatBytes(ev.fileSizeBytes)}</td>
                        <td style={tbl.td}>
                          <code style={{ fontSize: 11, color: C.muted }}>
                            {ev.contentHash.slice(0, 16)}…
                          </code>
                        </td>
                        <td style={{ ...tbl.td, color: C.muted }}>
                          {formatDateTime(ev.createdAt)}
                        </td>
                        <td style={tbl.td}>
                          <button
                            onClick={() => handleOpenEvidence(ev.id)}
                            style={{
                              background: 'transparent',
                              border: `1px solid ${C.border}`,
                              borderRadius: 6,
                              padding: '4px 10px',
                              cursor: 'pointer',
                              fontSize: 12,
                              color: C.link,
                            }}
                          >
                            {t('actions.view')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </>
        )}
      <IssueReviewModal
        open={activeReviewEntry !== null}
        mode={activeReviewEntry && hasAutomatedFindingReview(activeReviewEntry) ? 'edit' : 'review'}
        finding={activeReviewEntry ? {
          issueId: activeReviewEntry.issueId,
          code: activeReviewEntry.code,
          title: activeReviewEntry.title,
          impact: activeReviewEntry.impact,
          wcagCriteria: activeReviewEntry.wcagCriteria,
          wcagLevel: activeReviewEntry.wcagLevel,
          samplePageUrl: activeReviewEntry.samplePageUrl,
          occurrenceCount: activeReviewEntry.occurrenceCount,
        } : null}
        draft={activeReviewEntry ? getReviewDraft(activeReviewEntry) : {
          workflowState: 'open',
          severityOverride: '',
          note: '',
        }}
        submitting={activeReviewEntry !== null && reviewSavingIssueId === activeReviewEntry.issueId}
        error={reviewError}
        onClose={closeIssueReviewModal}
        onChange={(patch) => {
          if (!activeReviewEntry) {
            return;
          }
          updateReviewDraft(activeReviewEntry.issueId, patch);
        }}
        onSubmit={async () => {
          if (!activeReviewEntry) {
            return;
          }
          await handleSaveReview(activeReviewEntry);
        }}
      />
      {showExportUpgradeModal && (
        <FeatureLockModal
          title={t('feedback.exportLockedTitle')}
          description={t('feedback.exportLockedDescription')}
          benefits={[
            t('labels.exportBenefits.auditEvidence'),
            t('labels.exportBenefits.teamSharing'),
            t('labels.exportBenefits.remediationArtifacts'),
          ]}
          upgradeLabel={t('labels.upgradeToGrowth')}
          onUpgrade={() => {
            setShowExportUpgradeModal(false);
            window.location.href = complianceExportUpgradePath;
          }}
          onPricing={() => {
            setShowExportUpgradeModal(false);
            window.location.href = '/pricing';
          }}
          onClose={() => setShowExportUpgradeModal(false)}
        />
      )}
    </Container>
  );
}
    
